import { createStore } from 'zustand';
import type { StoreApi } from 'zustand';
import DataService from '../../services/data-service';
import type { AdminVendorsStore, VendorsFetcher } from './types';

const defaultFetcher: VendorsFetcher = () => new DataService().getAllVendors();

/**
 * Global admin-vendors store: loads the full vendor catalog once into memory so
 * the admin vendor-search screen can filter client-side. Modeled on the
 * stash-scoped store pattern, but global (not scope-filtered) and with its own
 * `error` / `isLoaded` state.
 *
 * @param fetchVendors injectable loader (defaults to DataService.getAllVendors)
 */
export function createAdminVendorsStore(
  fetchVendors: VendorsFetcher = defaultFetcher,
): StoreApi<AdminVendorsStore> {
  return createStore<AdminVendorsStore>((set, get) => {
    const fetchInto = async (): Promise<void> => {
      set({ loading: true, error: undefined });
      try {
        const vendors = await fetchVendors();
        set({ vendors, loading: false, isLoaded: true, error: undefined });
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to load vendors';
        // Keep any previously loaded vendors; surface the error for retry.
        set({ loading: false, isLoaded: false, error: message });
      }
    };

    return {
      vendors: [],
      loading: false,
      error: undefined,
      isLoaded: false,

      loadAll: async (): Promise<void> => {
        const { isLoaded, loading } = get();
        if (isLoaded || loading) return;
        await fetchInto();
      },

      reload: async (): Promise<void> => {
        await fetchInto();
      },
    };
  });
}
